import * as THREE from 'three'
import { TweenLite, Power2 } from 'gsap'

import EventEmitter from '../Utils/EventEmitter.js'

import fadeVertexShader from '../shaders/fade.vertex.glsl'
import fadeFragmentShader from '../shaders/fade.fragment.glsl'

import barrelVertexShader from '../shaders/barrel.vertex.glsl'
import barrelFragmentShader from '../shaders/barrel.fragment.glsl'


export default class Barrels extends EventEmitter
{
    constructor(_options = {})
    {
        super()

        // Set up
        this.$eventHub = _options.$eventHub
        this.mobile = _options.mobile
        this.camera = _options.camera
        this.sizes = _options.sizes
        this.time = _options.time
        this.cursor = _options.cursor
        this.ui = _options.ui
        this.loader = _options.loader

        // Container
        this.container = new THREE.Object3D()

        this.setRunning()
        this.setInteractions()
        this.setScene()
        this.setBarrels()
    }

    /**
     * Set running
     */
    setRunning()
    {
        this.running = false

        this.$eventHub.$on('experience.barrels.start', () =>
        {
            this.running = true
        })

        this.$eventHub.$on('experience.barrels.stop', () =>
        {
            this.running = false
        })
    }

    /**
     * Set interactions
     */
    setInteractions()
    {
        // Set up
        this.interactions = {}
        this.interactions.cursor = { x: 0, y: 0 }
        this.interactions.raycaster = new THREE.Raycaster()
        this.interactions.mouse = new THREE.Vector2()
        this.interactions.items = []
        this.interactions.intersection = null
        this.interactions.intersects = []
        this.interactions.currentReference = null
        this.interactions.downReference = null

        // Test intersections method
        this.interactions.testIntersections = (_x, _y) =>
        {
            // Update mouse vector
            this.interactions.mouse.x = (_x / this.sizes.width - 0.5) * 2
            this.interactions.mouse.y = - (_y / this.sizes.height - 0.5) * 2

            // Update raycaster
            this.interactions.raycaster.setFromCamera(this.interactions.mouse, this.camera.instance)

            // Get intersects
            const meshes = this.interactions.items.filter((item) => item.reference.interactive).map((item) => item.mesh)
            this.interactions.intersects = this.interactions.raycaster.intersectObjects(meshes)

            // No intersect
            if(this.interactions.intersects.length === 0)
            {
                this.interactions.intersection = null

                if(this.interactions.currentReference)
                {
                    if(typeof this.interactions.currentReference.cursorOut === 'function')
                    {
                        this.interactions.currentReference.cursorOut()
                    }
                    this.interactions.currentReference = null
                }
            }

            // Has intersects
            else
            {
                // Find item
                this.interactions.intersection = this.interactions.intersects[0]
                const item = this.interactions.items.find((_item) => _item.mesh === this.interactions.intersection.object)

                if(item)
                {
                    // Reference is different
                    if(item.reference !== this.interactions.currentReference && item.reference.interactive)
                    {
                        // Previous was a reference
                        if(this.interactions.currentReference && typeof this.interactions.currentReference.cursorOut === 'function')
                        {
                            this.interactions.currentReference.cursorOut()
                        }

                        // Update current
                        this.interactions.currentReference = item.reference

                        if(typeof this.interactions.currentReference.cursorIn === 'function')
                        {
                            this.interactions.currentReference.cursorIn()
                        }
                    }
                }
            }
        }

        if(this.mobile)
        {
            window.addEventListener('touchstart', (_event) =>
            {
                // Not running
                if(!this.running)
                {
                    return
                }

                this.interactions.cursor.x = _event.changedTouches[0].clientX
                this.interactions.cursor.y = _event.changedTouches[0].clientY

                this.interactions.testIntersections(this.interactions.cursor.x, this.interactions.cursor.y)

                if(this.interactions.currentReference)
                {
                    if(typeof this.interactions.currentReference.cursorClick === 'function')
                    {
                        this.interactions.currentReference.cursorClick()
                    }
                    this.interactions.downReference = this.interactions.currentReference
                }
            })
        }

        else
        {
            // Time tick
            this.time.on('tick', () =>
            {
                // Not running
                if(!this.running)
                {
                    return
                }

                this.interactions.cursor.x = this.cursor.x
                this.interactions.cursor.y = this.cursor.y

                this.interactions.testIntersections(this.cursor.x, this.cursor.y)
            })

            window.addEventListener('mousedown', () =>
            {
                // Not running
                if(!this.running)
                {
                    return
                }

                if(this.interactions.currentReference)
                {
                    if(typeof this.interactions.currentReference.cursorDown === 'function')
                    {
                        this.interactions.currentReference.cursorDown()
                    }
                    this.interactions.downReference = this.interactions.currentReference
                }
            })

            window.addEventListener('mouseup', () =>
            {
                // Not running
                if(!this.running)
                {
                    return
                }

                if(this.interactions.currentReference)
                {
                    if(typeof this.interactions.currentReference.cursorUp === 'function')
                    {
                        this.interactions.currentReference.cursorUp()
                    }

                    if(this.interactions.downReference === this.interactions.currentReference)
                    {
                        if(typeof this.interactions.currentReference.cursorClick === 'function')
                        {
                            this.interactions.currentReference.cursorClick()
                        }
                    }
                }

                this.interactions.downReference = null
            })
        }
    }

    /**
     * Set scene
     */
    setScene()
    {
        this.scene = {}

        // Floor
        this.scene.floor = {}

        this.scene.floor.material = new THREE.ShaderMaterial({
            transparent: true,
            flatShading: true,
            // depthWrite: false,
            // depthTest: false,
            blending: THREE.AdditiveBlending,
            uniforms:
            {
                uColor: { value: new THREE.Vector3(0.2, 0.2, 0.2) },

                uFocusPosition: { value: new THREE.Vector3(0, 0, 0) },
                uFocusDistance: { value: new THREE.Vector2(0, 10) }
            },
            vertexShader: fadeVertexShader,
            fragmentShader: fadeFragmentShader
        })

        this.scene.floor.geometry = this.loader.resources.barrelsSceneFloorModelObj.geometry

        this.scene.floor.mesh = new THREE.LineSegments(this.scene.floor.geometry, this.scene.floor.material)
        this.scene.floor.mesh.scale.x = 0.05
        this.scene.floor.mesh.scale.y = 0.05
        this.scene.floor.mesh.scale.z = 0.05
        this.container.add(this.scene.floor.mesh)

        // Delimiter
        this.scene.delimiter = {}

        this.scene.delimiter.material = new THREE.ShaderMaterial({
            transparent: true,
            flatShading: true,
            // depthWrite: false,
            // depthTest: false,
            blending: THREE.AdditiveBlending,
            uniforms:
            {
                uColor: { value: new THREE.Vector3(0.7, 0.7, 0.7) },

                uFocusPosition: { value: new THREE.Vector3(0, 0, - 2) },
                uFocusDistance: { value: new THREE.Vector2(0, 8) }
            },
            vertexShader: fadeVertexShader,
            fragmentShader: fadeFragmentShader
        })

        this.scene.delimiter.geometry = this.loader.resources.barrelsSceneDelimiterModelObj.geometry

        this.scene.delimiter.mesh = new THREE.LineSegments(this.scene.delimiter.geometry, this.scene.delimiter.material)
        this.scene.delimiter.mesh.scale.x = 0.05
        this.scene.delimiter.mesh.scale.y = 0.05
        this.scene.delimiter.mesh.scale.z = 0.05
        this.scene.delimiter.mesh.position.y = 0.002
        this.container.add(this.scene.delimiter.mesh)

        // Room
        this.scene.room = {}

        this.scene.room.material = new THREE.MeshBasicMaterial({
            transparent: true,
            flatShading: true,
            // depthWrite: false,
            // depthTest: false,
            blending: THREE.AdditiveBlending,
            color: 0x222222
        })

        this.scene.room.geometry = this.loader.resources.barrelsSceneRoomModelObj.geometry

        this.scene.room.mesh = new THREE.LineSegments(this.scene.room.geometry, this.scene.room.material)
        this.scene.room.mesh.scale.x = 0.05
        this.scene.room.mesh.scale.y = 0.05
        this.scene.room.mesh.scale.z = 0.05
        this.scene.room.mesh.position.y = 0.01
        this.container.add(this.scene.room.mesh)
    }

    /**
     * Set barrels
     */
    setBarrels()
    {
        // Set up
        this.barrels = {}
        this.barrels.items = []
        this.barrels.revealTimeout = null
        this.barrels.firstInteraction = false

        // Coordinates
        this.barrels.coordinates = [
            [-0.1, 0, 0],
            [0.1, 0, 0],
            [-0.043, 0, -0.06],
            [0.043, 0, -0.06]
        ]

        // Container
        this.barrels.container = new THREE.Object3D()
        this.container.add(this.barrels.container)

        // Texture
        this.barrels.heatGradientTexture = new THREE.Texture(this.loader.resources.heatGradientTexture)
        this.barrels.heatGradientTexture.needsUpdate = true

        // Each barrel
        for(const coordinates of this.barrels.coordinates)
        {
            const barrel = {}

            // Container
            barrel.container = new THREE.Object3D()
            barrel.container.position.x = coordinates[0]
            barrel.container.position.y = coordinates[1]
            barrel.container.position.z = coordinates[2]
            this.container.add(barrel.container)

            // Geometry
            barrel.geometry = this.loader.resources.barrelModelObj.geometry

            // Material
            barrel.material = new THREE.ShaderMaterial({
                transparent: true,
                flatShading: true,
                // depthWrite: false,
                // depthTest: false,
                blending: THREE.AdditiveBlending,
                side: THREE.DoubleSide,
                uniforms:
                {
                    uColor: { value: new THREE.Vector3(0.4, 0.4, 0.4) },
                    uHeatGradientTexture: { value: this.barrels.heatGradientTexture },

                    uFocusPosition: { value: new THREE.Vector3(0, 0, 0) },
                    uFocusDistance: { value: new THREE.Vector2(0, 0) }
                },
                vertexShader: barrelVertexShader,
                fragmentShader: barrelFragmentShader
            })

            // Mask
            barrel.maskMesh = new THREE.Mesh(
                new THREE.CylinderBufferGeometry(0.041, 0.041, 0.12, 32, 1),
                new THREE.MeshBasicMaterial({ color: 0x05070f })
            )
            barrel.maskMesh.position.y = 0.06
            barrel.container.add(barrel.maskMesh)

            // Mesh
            barrel.mesh = new THREE.LineSegments(barrel.geometry, barrel.material)

            barrel.mesh.scale.x = 0.042
            barrel.mesh.scale.y = 0.042
            barrel.mesh.scale.z = 0.042

            barrel.container.add(barrel.mesh)

            this.barrels.items.push(barrel)

            // Interaction
            barrel.found = false
            barrel.interactive = true
            barrel.cursorClick = () =>
            {
                // Not yet found
                if(!barrel.found)
                {
                    barrel.found = true
                    barrel.interactive = false

                    barrel.material.uniforms.uFocusPosition.value.x = this.interactions.intersection.point.x
                    barrel.material.uniforms.uFocusPosition.value.y = this.interactions.intersection.point.y
                    barrel.material.uniforms.uFocusPosition.value.z = this.interactions.intersection.point.z

                    window.clearTimeout(this.barrels.revealTimeout)
                    this.barrels.revealTimeout = window.setTimeout(() =>
                    {
                        TweenLite.to(barrel.material.uniforms.uFocusDistance.value, 1, { y: 0.06, ease: Power2.easeOut })
                    }, this.barrels.firstInteraction ? 800 : 1800)

                    this.barrels.firstInteraction = true

                    // Each other barrel
                    for(const item of this.barrels.items)
                    {
                        if(item !== barrel)
                        {
                            item.found = false
                            item.interactive = true
                            TweenLite.to(item.material.uniforms.uFocusDistance.value, 1, { y: 0, ease: Power2.easeOut })
                        }
                    }

                    this.$eventHub.$emit('experience.barrels.barrelCursorClick', this.interactions.cursor)
                }
            }
            barrel.cursorIn = () =>
            {
                this.$eventHub.$emit('experience.barrels.barrelCursorIn')
            }
            barrel.cursorOut = () =>
            {
                this.$eventHub.$emit('experience.barrels.barrelCursorOut')
            }
            this.interactions.items.push({ reference: barrel, mesh: barrel.maskMesh })
        }
    }

    /**
     * Reset
     */
    reset()
    {
        for(const item of this.barrels.items)
        {
            item.found = false
            item.interactive = true

            item.material.uniforms.uFocusPosition.value.x = 0
            item.material.uniforms.uFocusPosition.value.y = 0
            item.material.uniforms.uFocusPosition.value.z = 0

            item.material.uniforms.uFocusDistance.value.x = 0
            item.material.uniforms.uFocusDistance.value.y = 0
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/app-experience/Experience/Interactions/Barrels.js